import { PARTIAL_INFO_MESSAGE, DEFAULT_ROWS_PER_PAGE } from '../../constants/CommonConstants';
import { IFilterDTO, IClaimantListState, IClaimantListResponse} from "../../types/types";
import React, { useContext, useEffect, useState } from "react";
import SecuritiesTable, {
	IColumnHeader,
	ISecuritiesTableFieldMap,
	ISorter
} from "../../components/Shared/SecuritiesTable/SecuritiesTable";
import { UserContext, UserContextType } from "../../modules/User/UserContext";
import { createFilterDTOsFromColumnHeaders } from "../../utilities/filterCriteria";
import { getClaimantsForSubmissionAction, exportSubmissionClaimantsAction } from "./claimants.actions";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardContent, Grid, Skeleton, Theme } from "@mui/material";
import { TECHNICAL_ERROR_MESSAGE } from "../../constants/CommonConstants";
import { useLocation } from 'react-router-dom';
import ReactGA from "react-ga4";
import { getValidCmxAccountTypes } from "../Cmx/cmx.api";

export const claimStatuses = [
	{ "label": "Accepted", "value": 'Accepted' },
	{ "label": "Deficient", "value": 'Deficient' },
	{ "label": "Denied", "value": 'Denied' },
	{ "label": "Duplicate", "value": 'Duplicate' },
	{ "label": "Paid", "value": 'Paid' },
	{ "label": "Under Review", "value": 'Under Review' },
	{ "label": "Withdrawn", "value": 'Withdrawn' },
];


interface IClaimantListGridProps {
	submissionId: number;
	cmxCACaseNumber: number;
}

const claimantColumns: IColumnHeader[] = [
	{ field: "cmxClaimNumber", label: "Epiq Claim Number", parameterName: "cmxClaimNumber", type: "number", value: "", allowFilter: true },
	{ field: "accountNumber", label: "Account Number", parameterName: "accountNumber", type: "string", value: "", allowFilter: true },
	{ field: "accountType", label: "Account Type", parameterName: "accountTypes", type: "selectManyString", enumValues: [], value: "", allowFilter: true },
	{ field: "keyName", label: "Beneficial Owner", parameterName: "keyName", type: "string", value: "", allowFilter: true },
	{ field: "status", label: "Claim Status", parameterName: "claimStatusList", type: "selectManyString", enumValues: [], value: "", allowFilter: true },
	{ field: 'actions', label: 'Actions', hideHeader: true }
];

const defaultSort: ISorter = {
	property: "cmxClaimNumber",
	order: "asc",
};

let sortInfo: ISorter = { ...defaultSort };

let filterBackup: IFilterDTO[];

const ClaimantListGrid = ({ submissionId, cmxCACaseNumber }: IClaimantListGridProps) => {
	const { isAuthenticated, isInitialized } = useContext(UserContext) as UserContextType;
	const dispatch = useDispatch();
	const cmxCaseNumber: number = cmxCACaseNumber;
	let initialFilter: IFilterDTO[];
	let claimantListState: IClaimantListState = useSelector((state: any) => state.claimantListState);
	let claimantResponse: IClaimantListResponse = useSelector((state: any) => {
		return {
			claimants: state.claimantListState.claimants,
			totalRowCount: state.claimantListState.totalRowCount
		};
	});


	const [cmxAccountTypes, setCmxAccountTypes] = useState([{ label: "Loading...", value: "_" }]);
	const [cmxAccountTypesFetched, setCmxAccountTypesFetched] = useState<boolean>(false);

	claimantColumns[2].enumValues = cmxAccountTypes;
	claimantColumns[4].enumValues = claimStatuses;
	const location = useLocation();

	useEffect(() => {
		const path = location.pathname + location.search;
		ReactGA.send({ hitType: "pageview", page: path, title: "Claimant List Page" });
	}, [location]);
	
	useEffect(() => {

		if (isInitialized && isAuthenticated) {
				initialFilter = createFilterDTOsFromColumnHeaders(claimantColumns);
				filterBackup = [...initialFilter];

				dispatch(getClaimantsForSubmissionAction({
					submissionId: submissionId,
					cmxCaseNumber: cmxCaseNumber,
					filter: initialFilter,
					sorter: sortInfo,
					pageNumber: 1, // Always start at page 1 on initial load
					rowsPerPage: DEFAULT_ROWS_PER_PAGE
				}));

		}

	}, [isAuthenticated, isInitialized, dispatch]);

	useEffect(() => {

		if (cmxCaseNumber && !cmxAccountTypesFetched) {

			getValidCmxAccountTypes(cmxCaseNumber)
				.then(data => { 
								if (data) {
									setCmxAccountTypes(data.map((item: any) => {
										return { label: item, value: item };
									}))

									setCmxAccountTypesFetched(true);
								} else {
									console.log('No data returned');
									setCmxAccountTypes([{ label: "No results...", value: "_" }])
								}
					}
				)
				.catch(console.error);
		}

	}, [cmxCaseNumber, cmxAccountTypesFetched]);

	const handleResetFilter = () => {
		sortInfo = { ...defaultSort };
	};

	const fetchFilteredClaimantList = (filters: IFilterDTO[], pageNumber?: number, rowsPerPage?: number) => {

		filterBackup = [...filters];

		// pageNumber is now 1-based from SecuritiesTable
		const page = pageNumber !== undefined ? pageNumber : 1;

		dispatch(getClaimantsForSubmissionAction({
			submissionId: submissionId,
			cmxCaseNumber: cmxCaseNumber,
			filter: filters,
			sorter: sortInfo,
			pageNumber: page, // Pass 1-based to API
			rowsPerPage: rowsPerPage
		}));
	};

	const exportFilteredClaimantList = () => {
		dispatch(exportSubmissionClaimantsAction({
			submissionId: submissionId,
			filter: filterBackup,
			cmxCaseNumber: cmxCACaseNumber
		}));
	};

	const hasAllData = (): boolean => {
		return (claimantResponse.claimants && claimantResponse.claimants.length > 0 ) ? true : false
	}

	const buildClaimantRows = (): ISecuritiesTableFieldMap[] => {
		var claimantRows: ISecuritiesTableFieldMap[] = [];

		claimantResponse.claimants &&
			claimantResponse.claimants.forEach((item, index) => {
								

				let newItem: ISecuritiesTableFieldMap = {
					cmxClaimNumber: { type: "number", value: item.cmxClaimNumber },
					accountNumber: { type: "string", value: item.accountNumber },
					accountType: { type: "string", value: item.accountType },
					keyName: { type: "string", value: item.keyName },
					status: { type: "string", value: item.claimStatus},
					actions: { type: "string", value: '' }
				};

				claimantRows.push(newItem);
			});


		return claimantRows;
	};

	const claimantRows = buildClaimantRows();

	return (
		<>
			{(claimantListState.isLoaded && claimantRows) ?
				(<>
					<SecuritiesTable
						columns={claimantColumns}
						rows={claimantRows}
						totalRowCount={claimantResponse.totalRowCount}
						sorter={sortInfo}
						paging={'server'}
						pageNumber={claimantListState.pageNumber}  // Already 1-based from API
						rowsPerServerPage={claimantListState.rowsPerPage}
						showFilter={true}
						dataFetchFunc={fetchFilteredClaimantList}
						maxTableHeight={350}
						dataExportFunc={exportFilteredClaimantList}
						enableExport={true}
						resetFilterFunc={handleResetFilter}
					/>
					{
						(!hasAllData() && !claimantListState.error) &&
						<p style={{ color: 'red' }}> {PARTIAL_INFO_MESSAGE}</p>
					}
					{
						(claimantListState.error) &&
						<p style={{ color: 'red' }}>{TECHNICAL_ERROR_MESSAGE}</p>
					}
				</>)
				:
				(<Card>
					<CardContent>
						{[...Array(DEFAULT_ROWS_PER_PAGE)].map((elementInArray, index) => (
							<Grid container spacing={2} key={index}>
								<Grid item xs={2}>
									<Skeleton animation="wave" />
								</Grid>
								<Grid item xs={4}>
									<Skeleton animation="wave" />
								</Grid>
								<Grid item xs={2}>
									<Skeleton animation="wave" />
								</Grid>
								<Grid item xs={2}>
									<Skeleton animation="wave" />
								</Grid>
								<Grid item xs={2}>
									<Skeleton animation="wave" />
								</Grid>
							</Grid>
						))}
					</CardContent>
				</Card>)
			}
		</>
	);
};

export default ClaimantListGrid;